import { FC, useContext, useMemo, useState } from 'react';

import { H2, Preloader, Tooltip as TooltipKit } from '@smartshell/ui-kit';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';

import { RevenueTooltip } from '../Revenue/RevenueTooltip';
import { formatMoney } from '../utils';
import { LABEL_ORDER, LABEL_TRANSLATION_MAP } from './constants';
import s from './Expense.module.scss';
import { IChartDataItem, IExpenseProps } from './interfaces';

export const Expense: FC<IExpenseProps> = ({ labels, values, loading }) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);

  const getChartData = (
    labels: string[] | undefined,
    values: number[] | undefined
  ): IChartDataItem[] => {
    if (!labels || !values) return [];

    return labels
      .map((label, index) => {
        if (label.includes('_percent')) return null;

        const key = label as keyof typeof LABEL_TRANSLATION_MAP;
        const translationKey = LABEL_TRANSLATION_MAP[key];

        if (!translationKey) return null;

        return {
          name: t(translationKey),
          value: values[index] || 0,
          originalKey: key,
        };
      })
      .filter(
        (
          item
        ): item is {
          name: string;
          value: number;
          originalKey: keyof typeof LABEL_TRANSLATION_MAP;
        } => !!item
      )
      .filter((item) => item.value !== 0)
      .sort((a, b) => LABEL_ORDER[a.originalKey] - LABEL_ORDER[b.originalKey])
      .map(({ originalKey, ...rest }) => rest);
  };

  const chartData = useMemo(
    () => getChartData(labels, values?.values),
    [labels, values, t]
  );

  const [focusBar, setFocusBar] = useState<number | null>(null);

  const sum = chartData.reduce((currentSum, currentNumber) => {
    return currentSum + currentNumber.value;
  }, 0);

  return (
    <div className={s.container}>
      <div className={s.header}>
        <H2 className={s.title}>{t('visitors.finance.customer.clients')}</H2>
        <TooltipKit contentClassName={s.tooltip} content={t('expense.title')} />
      </div>

      {(labels === undefined || values?.values.every((val) => val === 0)) &&
      !loading ? (
        <div className={s.placeholder}>
          {values?.values.every((val) => val === 0)
            ? t('visitors.empty.data')
            : t('report.placeholder.emptyDates')}
        </div>
      ) : (
        <Preloader loading={loading}>
          <ResponsiveContainer className={s.data} width="100%" height="100%">
            <BarChart
              data={chartData}
              onMouseMove={(state) => {
                if (state.isTooltipActive) {
                  setFocusBar(state.activeTooltipIndex ?? null);
                } else {
                  setFocusBar(null);
                }
              }}
            >
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                dy={8}
                tick={{ fill: 'var(--text-primary)' }}
                fontSize={12}
              />
              <YAxis
                tickFormatter={(value) => `${value} ${currency.sign}`}
                axisLine={false}
                tickLine={false}
                width={130}
                dx={-55}
                tick={{
                  fill: 'var(--text-primary)',
                  style: { whiteSpace: 'nowrap' },
                }}
                fontSize={12}
              />
              <Tooltip
                cursor={{ fill: 'var(--bg-item-secondary)' }}
                content={({ payload }) => {
                  const rawValue = payload?.[0]?.value;
                  const value = typeof rawValue === 'number' ? rawValue : 0;
                  const label = payload?.[0]?.payload?.name;

                  const percentage =
                    !loading && typeof sum === 'number' && sum > 0
                      ? `${((value / sum) * 100).toFixed(2)}%`
                      : '-';

                  return (
                    <RevenueTooltip
                      title={String(label)}
                      text_1={t('expense')}
                      value_1={`${formatMoney(value, currency.sign)}`}
                      text_2={t('percentage.total.revenue')}
                      value_2={percentage}
                      variant="primary"
                    />
                  );
                }}
              />

              <Bar
                fill="var(--bg-blue-10)"
                dataKey="value"
                radius={[4, 4, 0, 0]}
                isAnimationActive={false}
                maxBarSize={200}
              >
                <LabelList
                  dataKey="value"
                  position={'inside'}
                  content={(props) => {
                    const { x, y, width, value } = props;
                    const percent =
                      typeof sum === 'number' && sum > 0
                        ? `${((Number(value) / sum) * 100).toFixed(0)}%`
                        : '';
                    const XPosition = Number(x) + Number(width) / 2;
                    const YPosition = Number(y) - 8;

                    return (
                      <text
                        x={XPosition}
                        y={YPosition}
                        fill="var(--text-primary)"
                        fontSize="18"
                        textAnchor="middle"
                      >
                        {percent}
                      </text>
                    );
                  }}
                />
                {chartData?.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      focusBar === index
                        ? 'var(--bg-blue)'
                        : 'var(--bg-blue-10)'
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Preloader>
      )}
    </div>
  );
};
